/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import {
  getExpiresAt,
  getTokens,
  storeTokens,
} from '../common/localStorageHelper';
import { getApi, getOidcApi } from '../common/requestHelper';
import {
  GrantTokenDto,
  GrantTokenResponseDto,
  UserApi,
} from '@ink-ai/insight-service-sdk';
import { isAxiosError } from 'axios';
import { configuration } from './configuration';
import { RootState } from '.';
import { jwtDecode } from 'jwt-decode';
import { OIDC_SCOPE } from '../common/authHelper';

export enum AuthType {
  AMAZON_FED = 'AMAZON_FED',
}

// JWT token解析辅助函数
const parseJwtToken = (token: string) => {
  try {
    const decoded = jwtDecode<{
      name?: string;
      firstname?: string;
      lastname?: string;
      groups?: string | string[];
      group?: string | string[];
      [key: string]: any;
    }>(token);
    console.log('JWT Token parsed:', decoded);
    return decoded;
  } catch(error) {
    console.error('Failed to parse JWT token:', error);
    return null;
  }
}

const getFullName = (claims: any): string | undefined => {
  if (!claims) return undefined;
  const n = typeof claims.name === 'string' ? claims.name.trim() : '';
  if (n) return n;
  const firstSource = claims.firstname ?? claims['first name'];
  const lastSource = claims.lastname ?? claims['last name'];
  const first = typeof firstSource === 'string' ? firstSource.trim() : '';
  const last = typeof lastSource === 'string' ? lastSource.trim() : '';
  const parts = [first, last].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
};

// 规范化 group 声明，使其在单权限时为 string，多权限时为 string[]
const normalizeGroupClaim = (
  group: unknown,
): string | string[] | undefined => {
  if (group == null) return undefined;
  if (Array.isArray(group)) {
    const arr = group.filter((g) => typeof g === 'string') as string[];
    return arr.length ? arr : undefined;
  }
  if (typeof group === 'string') {
    const maybeMulti = group.split(',').map((s) => s.trim()).filter(Boolean);
    if (maybeMulti.length > 1) return maybeMulti;
    return group.trim();
  }
  return undefined;
};

export type AuthState = {
  username?: string;
  userFullName?: string;
  userEmail?: string;
  userId?: string;
  loggedIn: boolean;
  authType?: AuthType;
  loading: boolean;
  grantTokenLoading: boolean;
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt?: number;
  instanceId: string;
  loginErrorMessageOpen: boolean;
  loginErrorMessage?: string;
  group?: string | string[];
  name?: string;
  // Consecutive refresh_token grant failures. A single failure is often
  // transient (network blip, gateway hiccup); we only force logout after
  // several in a row so a still-valid refresh token isn't discarded.
  refreshFailureCount?: number;
};

const initialState: AuthState = {
  username: undefined,
  loggedIn: false,
  loading: false,
  authType: undefined,
  loginErrorMessageOpen: false,
  instanceId: uuidv4(),
  grantTokenLoading: false,
};

const getInitState = (): AuthState => {
  const tokens = getTokens();
  // Restore loggedIn from a non-expired stored token so a browser refresh keeps
  // the session instead of resetting loggedIn=false — which was making refresh
  // hang on WebSocket "Connecting...", bounce to /login, and load an empty task
  // list. Expiry is still checked here, and a token that looks valid but was
  // revoked is caught by the 401 interceptor (forceRenewToken), so this is safe.
  const hasValidToken =
    !!tokens.access_token &&
    (!tokens.expires_at || tokens.expires_at > Math.floor(Date.now() / 1000));

  const state = {
    ...initialState,
    accessToken: tokens.access_token,
    expiresAt: tokens.expires_at,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token,
    loggedIn: hasValidToken,
  }

  // 解析accessToken和idToken
  if (tokens.access_token) {
    const parsed = parseJwtToken(tokens.access_token);
    const fullName = getFullName(parsed);
    if (fullName) state.name = fullName;
  }

  if (tokens.id_token) {
    const parsed = parseJwtToken(tokens.id_token);
    const normalized = normalizeGroupClaim(parsed?.['cognito:groups'] ?? parsed?.groups ?? parsed?.group);
    if (normalized) state.group = normalized;
  }

  return state;
};

export type grantTokenInput = {
  grant_type: 'authorization_code' | 'refresh_token';
  code?: string;
  refresh_token?: string;
};

export const grantToken = createAsyncThunk(
  'auth/grantToken',
  async (body: GrantTokenDto, { dispatch, getState }) => {
    const state = getState() as RootState;
    dispatch(auth.actions.changeLoading(true));
    try {
      const response = await getOidcApi().grantToken({
        ...body,
        redirect_uri: `${state.app.addInHost}/oidc.html`,
        scope: OIDC_SCOPE,
      });
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error) && error.response) {
        dispatch(
          auth.actions.setLoginErrorMessage(error.response.data.message),
        );
      } else {
        dispatch(auth.actions.setLoginErrorMessage(error.message));
      }
      console.log(error);
      throw error;
    }
  },
);

export const login = createAsyncThunk('auth/Login', async (_, { dispatch }) => {
  const userApi = await getApi(UserApi);
  try {
    const { data } = await userApi.login();
    dispatch(configuration.actions.updateUserConfiguration(data.configuration));
    return data;
  } catch (error: any) {
    dispatch(auth.actions.signOut());
    if (isAxiosError(error) && error.response) {
      dispatch(auth.actions.setLoginErrorMessage(error.response.data.message));
    } else {
      dispatch(auth.actions.setLoginErrorMessage(error.message));
    }
    console.error(error);
    throw error;
  }
});

export const auth = createSlice({
  name: 'auth',
  initialState: getInitState(),
  reducers: {
    setInstanceId(state, { payload }: PayloadAction<string>) {
      state.instanceId = payload;
    },
    setLoginErrorMessage(state, { payload }: PayloadAction<string>) {
      state.loginErrorMessageOpen = true;
      state.loginErrorMessage = payload;
    },
    closeLoginErrorMessage(state) {
      state.loginErrorMessageOpen = false;
    },
    inputUsername(state, { payload }: PayloadAction<string>) {
      state.username = payload;
    },
    changeLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
    },
    signOut() {
      storeTokens(null);
      return initialState;
    },
    tokenGranted(state, { payload }: PayloadAction<GrantTokenResponseDto>) {
      state.accessToken = payload.access_token;
      state.idToken = payload.id_token;
      state.refreshToken = payload.refresh_token ?? state.refreshToken;
      state.expiresAt = getExpiresAt(payload);
      storeTokens(payload);

      // 解析accessToken和idToken
      if (payload.access_token) {
        const parsed = parseJwtToken(payload.access_token);
        const fullName = getFullName(parsed);
        if (fullName) state.name = fullName;
      }

      if (payload.id_token) {
        const parsed = parseJwtToken(payload.id_token);
        const normalized = normalizeGroupClaim(parsed?.['cognito:groups'] ?? parsed?.groups ?? parsed?.group);
        if (normalized) state.group = normalized;
      }
    },
    setAuth(_state, { payload }: PayloadAction<AuthState>) {
      console.log('setAuthStore', payload);
      return payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(grantToken.pending, (state) => {
      state.grantTokenLoading = true;
    });
    builder.addCase(grantToken.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.grantTokenLoading = false;
      // A successful grant (authorization_code OR refresh_token) means the
      // session is valid — keep the user logged in. Previously this was never
      // set on refresh, so a silent renewal didn't restore loggedIn if it had
      // been flipped false, forcing an unnecessary re-login.
      state.loggedIn = true;
      state.refreshFailureCount = 0;
      state.accessToken = payload.access_token;
      state.idToken = payload.id_token;
      state.refreshToken = payload.refresh_token ?? state.refreshToken;
      state.expiresAt = getExpiresAt(payload);
      storeTokens(payload);
      
      // 解析accessToken和idToken
      if (payload.access_token) {
        const parsed = parseJwtToken(payload.access_token);
        const fullName = getFullName(parsed);
        if (fullName) state.name = fullName;
      }

      if (payload.id_token) {
        const parsed = parseJwtToken(payload.id_token);
        const normalized = normalizeGroupClaim(parsed?.['cognito:groups'] ?? parsed?.groups ?? parsed?.group);
        if (normalized) state.group = normalized;
      }
    });
    builder.addCase(grantToken.rejected, (state, action) => {
      state.loading = false;
      state.grantTokenLoading = false;
      // A refresh_token grant failed. Don't nuke the session on the first
      // failure — refresh failures are often transient (network blip, gateway
      // 5xx, a request fired right at expiry). The refresh token itself is
      // usually still valid, so KEEP it and let the next attempt retry. Only
      // after several consecutive failures do we conclude the token is really
      // dead and force a re-login.
      if (action.meta?.arg?.grant_type === 'refresh_token') {
        const failures = (state.refreshFailureCount ?? 0) + 1;
        state.refreshFailureCount = failures;
        if (failures >= 3) {
          storeTokens(null);
          state.accessToken = undefined;
          state.idToken = undefined;
          state.refreshToken = undefined;
          state.expiresAt = undefined;
          state.loggedIn = false;
          state.refreshFailureCount = 0;
        }
      }
    });
    builder.addCase(login.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(login.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.userId = payload.uuid;
      state.username = payload.username;
      state.userEmail = payload.email;
      state.userFullName = payload.name;
      state.loggedIn = true;
    });
    builder.addCase(login.rejected, (state) => {
      state.loading = false;
    });
  },
});
